import React from 'react';
import { Calendar, MessageCircle } from 'lucide-react';
import { Task } from '../../types';
import { Badge, getPriorityVariant, priorityLabels } from '../common/Badge';
import { format, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TaskPeople } from './TaskPeople';

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
}

const statusStyles: Record<Task['status'], { label: string; color: string }> = {
  todo: { label: 'Não iniciado', color: '#C24914' },
  in_progress: { label: 'Em andamento', color: '#FDAB3D' },
  in_review: { label: 'Parado', color: '#E2445C' },
  done: { label: 'Feito', color: '#00C875' },
};

export function TaskCard({ task, onClick }: TaskCardProps) {
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done';
  const status = statusStyles[task.status];

  return (
    <div
      onClick={() => onClick(task)}
      className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-all hover:border-primary-200 group"
    >
      {/* Priority indicator */}
      <div className="flex items-start mb-2">
        <p className="text-sm font-medium text-gray-900 line-clamp-2 flex-1 group-hover:text-primary-600 transition-colors">
          {task.title}
        </p>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="inline-flex items-center gap-1.5 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
          <span className="w-1 h-4 rounded-sm" style={{ backgroundColor: status.color }} />
          {status.label}
        </span>
        <Badge variant={getPriorityVariant(task.priority)} className="text-xs">
          {priorityLabels[task.priority] || task.priority}
        </Badge>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <TaskPeople task={task} />
        </div>
        <div className="flex items-center gap-2">
          {task.comment_count && Number(task.comment_count) > 0 && (
            <span className="flex items-center gap-1">
              <MessageCircle size={11} />
              {task.comment_count}
            </span>
          )}
          {task.due_date && (
            <span className={`flex items-center gap-1 ${isOverdue ? 'text-gray-700' : ''}`}>
              <Calendar size={11} />
              {format(new Date(task.due_date), 'd MMM', { locale: ptBR })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
